// O rasto de um login que atravessa a Google e volta.
//
// Dentro de uma folha de autenticação do iOS não há consola nem inspetor, e
// o único canal para fora é o `okazip://`. Uma linha por tentativa custa um
// desbloqueio do telemóvel e uma ida à Google por facto — então a página vai
// deixando marcas em `sessionStorage`, que sobrevive à viagem, e o app
// recebe a história toda de uma vez quando algo falha.
//
// Só nomes e domínios. Nenhum valor: as chaves do Firebase guardam
// credenciais.

const TRAIL = 'wa.trail';
const GRIPES = 'wa.gripes';

/**
 * O que a página se queixou, guardado onde sobrevive à viagem.
 *
 * Um `getRedirectResult` que devolve vazio não diz porquê, mas o SDK
 * costuma reclamar antes — e dentro da folha de autenticação do iOS não há
 * consola onde ler a reclamação. Então ela é recolhida aqui e sai pelo
 * `okazip://` com o resto.
 *
 * Só erros e avisos, e cortados: o canal é uma URL.
 */
function gripe(what: string): void {
  try {
    const now = sessionStorage.getItem(GRIPES) ?? '';
    if (now.length > 700) return;
    sessionStorage.setItem(GRIPES, `${now}${now ? ' | ' : ''}${what.slice(0, 160)}`);
  } catch { /* private mode */ }
}

export function listen(): void {
  if (typeof window === 'undefined') return;
  addEventListener('error', (e) => gripe(`err:${e.message}`));
  addEventListener('unhandledrejection', (e) =>
    gripe(`rej:${String((e.reason as { code?: string })?.code ?? e.reason)}`));
  for (const level of ['error', 'warn'] as const) {
    const was = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      gripe(`${level}:${args.map((a) => String((a as { message?: string })?.message ?? a)).join(' ')}`);
      was(...args);
    };
  }
}

export function mark(step: string): void {
  try {
    const now = sessionStorage.getItem(TRAIL) ?? '';
    sessionStorage.setItem(TRAIL, `${now}${now ? ' > ' : ''}${step}`);
  } catch { /* private mode */ }
}

/** As chaves que o Firebase deixou, sem os valores. */
function keys(s: Storage): string {
  try {
    const found = Object.keys(s)
      .filter((k) => k.startsWith('firebase'))
      .map((k) => k.replace(/AIza[\w-]+/g, 'KEY').replace(/:\[DEFAULT\]$/, '')
        .replace('firebase:', ''));
    return found.join(',') || 'none';
  } catch { return 'unreadable'; }
}

/** Posto pelo chamador: havia utilizador do Firebase, ou não. */
let firebaseUser = 'unchecked';
export function noteUser(uid: string | null): void {
  firebaseUser = uid ?? 'none';
}

export function whatCameBack(): string {
  let trail = 'none';
  try { trail = sessionStorage.getItem(TRAIL) ?? 'none'; } catch { /* private mode */ }
  let referrer = 'none';
  try { referrer = new URL(document.referrer || 'about:blank').host || 'none'; } catch { /* opaque */ }
  // Voltar do provedor e voltar para trás no histórico deixam a página no
  // mesmo sítio com o mesmo aspeto. Esta é a única coisa que os separa:
  // uma navegação de volta declara-se `back_forward`.
  let how = 'unknown';
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    how = nav?.type ?? 'unknown';
  } catch { /* sem a API */ }
  let gripes = 'none';
  try { gripes = sessionStorage.getItem(GRIPES) ?? 'none'; } catch { /* private mode */ }
  return [
    `nav=${how}`,
    `said=[${gripes}]`,
    `fbuser=${firebaseUser}`,
    `trail=[${trail}]`,
    `url=${location.pathname}${location.search.slice(0, 120)}`,
    `from=${referrer}`,
    `session=${keys(sessionStorage)}`,
    `local=${keys(localStorage)}`,
  ].join(' ');
}
